import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  RefreshCw,
  FileText,
  ClipboardCheck,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MinusCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { formatDateOnly } from '@/lib/date-utils';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  useAuditPrep,
  useFM333Data,
  useGenerateResponses,
  useGenerateFM333,
  useGenerateFM312,
  useUpdateClauseResponse,
  downloadFM333,
  downloadFM312,
} from '@/hooks/use-audit-prep';
import type { AuditClauseResponse, ClauseResponseValue, FM333Data } from '@/lib/types';
import { getAuditTypeLabel } from '@/lib/audit-types';

type TabId = 'fm333' | 'fm312' | 'summary';

const RESPONSE_CONFIG: Record<ClauseResponseValue, { icon: React.ReactNode; color: string; label: string }> = {
  yes: { icon: <CheckCircle className="h-4 w-4" />, color: 'text-green-600', label: 'Yes' },
  no: { icon: <XCircle className="h-4 w-4" />, color: 'text-red-600', label: 'No' },
  partial: { icon: <AlertTriangle className="h-4 w-4" />, color: 'text-yellow-600', label: 'Partial' },
  not_applicable: { icon: <MinusCircle className="h-4 w-4" />, color: 'text-gray-400', label: 'N/A' },
};

const STATUS_BADGE: Record<string, string> = {
  in_progress: 'bg-blue-100 text-blue-800',
  ready_for_review: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
};

// ---------------------------------------------------------------------------
// FM-333 Tab
// ---------------------------------------------------------------------------

function FM333Tab({ auditPrepId, fm333Data, isLoading }: {
  auditPrepId: number;
  fm333Data: FM333Data | undefined;
  isLoading: boolean;
}) {
  const generateFM333 = useGenerateFM333();

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  if (!fm333Data) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-gray-500">Unable to load FM-333 data.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          onClick={() => generateFM333.mutate(auditPrepId)}
          disabled={generateFM333.isPending}
        >
          <FileText className="h-4 w-4 mr-2" />
          {generateFM333.isPending ? 'Generating...' : 'Generate FM-333 PDF'}
        </Button>
      </div>

      {/* Company Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1. Company Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="font-medium text-gray-500">Registered Name</dt>
              <dd className="mt-1">{fm333Data.company.name || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Trading Name</dt>
              <dd className="mt-1">{fm333Data.company.trading_name || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Registration No</dt>
              <dd className="mt-1">{fm333Data.company.registration_number || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">VAT Number</dt>
              <dd className="mt-1">{fm333Data.company.vat_number || '-'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="font-medium text-gray-500">Address</dt>
              <dd className="mt-1">{fm333Data.company.registered_address || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Website</dt>
              <dd className="mt-1">{fm333Data.company.website || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Legal Structure</dt>
              <dd className="mt-1">{fm333Data.company.legal_structure || '-'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Framework */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">2. Framework &amp; Scope</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="font-medium text-gray-500">Framework</dt>
              <dd className="mt-1">{fm333Data.framework.type || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Membership No</dt>
              <dd className="mt-1">{fm333Data.framework.membership_number || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Status</dt>
              <dd className="mt-1">{fm333Data.framework.membership_status || '-'}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Last Audit</dt>
              <dd className="mt-1">{formatDateOnly(fm333Data.framework.last_audit_date, '-')}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Next Audit Due</dt>
              <dd className="mt-1">{formatDateOnly(fm333Data.framework.next_audit_due, '-')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Personnel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">3. Nominated Personnel</CardTitle>
        </CardHeader>
        <CardContent>
          {fm333Data.personnel.length === 0 ? (
            <p className="text-sm text-gray-500">No nominated personnel recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-gray-500">Name</th>
                    <th className="pb-2 font-medium text-gray-500">Role</th>
                    <th className="pb-2 font-medium text-gray-500">Email</th>
                    <th className="pb-2 font-medium text-gray-500">Cert Valid</th>
                  </tr>
                </thead>
                <tbody>
                  {fm333Data.personnel.map((p, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{p.name}</td>
                      <td className="py-2">{p.role}</td>
                      <td className="py-2 text-gray-600">{p.email}</td>
                      <td className="py-2">
                        {p.cert_validated ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Insurance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">4. Insurance Policies</CardTitle>
        </CardHeader>
        <CardContent>
          {fm333Data.insurance.length === 0 ? (
            <p className="text-sm text-gray-500">No active insurance policies recorded.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-gray-500">Policy</th>
                    <th className="pb-2 font-medium text-gray-500">Type</th>
                    <th className="pb-2 font-medium text-gray-500">Insurer</th>
                    <th className="pb-2 font-medium text-gray-500">Expires</th>
                    <th className="pb-2 font-medium text-gray-500">Rope Access</th>
                  </tr>
                </thead>
                <tbody>
                  {fm333Data.insurance.map((ip, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2">{ip.policy_name}</td>
                      <td className="py-2">{ip.policy_type}</td>
                      <td className="py-2 text-gray-600">{ip.insurer}</td>
                      <td className="py-2">{formatDateOnly(ip.expiry_date)}</td>
                      <td className="py-2">
                        {ip.covers_rope_access ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Yes</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">No</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Procedures */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">5. Documented Procedures</CardTitle>
        </CardHeader>
        <CardContent>
          {fm333Data.procedures.length === 0 ? (
            <p className="text-sm text-gray-500">No approved procedures found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-gray-500">Document No</th>
                    <th className="pb-2 font-medium text-gray-500">Title</th>
                    <th className="pb-2 font-medium text-gray-500">Category</th>
                    <th className="pb-2 font-medium text-gray-500">Version</th>
                  </tr>
                </thead>
                <tbody>
                  {fm333Data.procedures.map((p, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 font-mono text-xs">{p.document_number}</td>
                      <td className="py-2">{p.title}</td>
                      <td className="py-2 capitalize">{p.category}</td>
                      <td className="py-2">v{p.version}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FM-312 Tab — Clause Checklist
// ---------------------------------------------------------------------------

function ClauseCard({ clause, auditPrepId }: {
  clause: AuditClauseResponse;
  auditPrepId: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const updateMutation = useUpdateClauseResponse();
  const config = RESPONSE_CONFIG[clause.response];

  const handleResponseChange = (value: string) => {
    updateMutation.mutate({
      auditPrepId,
      responseId: clause.id,
      data: { response: value as ClauseResponseValue },
    });
  };

  const handleEvidenceChange = (value: string) => {
    updateMutation.mutate({
      auditPrepId,
      responseId: clause.id,
      data: { evidence_description: value },
    });
  };

  const handleNotesChange = (value: string) => {
    updateMutation.mutate({
      auditPrepId,
      responseId: clause.id,
      data: { notes: value },
    });
  };

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="font-mono text-xs text-gray-500 w-12 flex-shrink-0">
          {clause.clause_ref}
        </span>
        <span className="flex-1 text-sm font-medium truncate">
          {clause.clause_title}
        </span>
        <span className={`flex items-center gap-1 text-sm ${config.color}`}>
          {config.icon}
          <span className="hidden sm:inline">{config.label}</span>
        </span>
        {clause.system_auto_response && clause.system_auto_response !== clause.response && (
          <Badge variant="outline" className="text-xs flex-shrink-0">
            System: {RESPONSE_CONFIG[clause.system_auto_response].label}
          </Badge>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t space-y-4 pt-4">
          <p className="text-sm text-gray-700">{clause.question_text}</p>

          {clause.system_auto_response && (
            <div className="flex items-center gap-2 text-xs bg-gray-50 px-3 py-2 rounded">
              <span className="text-gray-500">System suggestion:</span>
              <span className={RESPONSE_CONFIG[clause.system_auto_response].color}>
                {RESPONSE_CONFIG[clause.system_auto_response].label}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Response</label>
              <Select value={clause.response} onValueChange={handleResponseChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Evidence Description</label>
            <Textarea
              defaultValue={clause.evidence_description}
              onBlur={(e) => {
                if (e.target.value !== clause.evidence_description) {
                  handleEvidenceChange(e.target.value);
                }
              }}
              placeholder="Describe how this requirement is met..."
              rows={2}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Notes</label>
            <Textarea
              defaultValue={clause.notes}
              onBlur={(e) => {
                if (e.target.value !== clause.notes) {
                  handleNotesChange(e.target.value);
                }
              }}
              placeholder="Additional notes..."
              rows={2}
              className="text-sm"
            />
          </div>
        </div>
      )}
    </Card>
  );
}

function FM312Tab({ auditPrepId, responses }: {
  auditPrepId: number;
  responses: AuditClauseResponse[];
}) {
  const generateResponses = useGenerateResponses();
  const generateFM312 = useGenerateFM312();

  const total = responses.length;
  const answered = responses.filter((r) => r.response !== 'not_applicable').length;
  const yesCount = responses.filter((r) => r.response === 'yes').length;
  const noCount = responses.filter((r) => r.response === 'no').length;
  const partialCount = responses.filter((r) => r.response === 'partial').length;

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={() => generateResponses.mutate(auditPrepId)}
          disabled={generateResponses.isPending}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${generateResponses.isPending ? 'animate-spin' : ''}`} />
          {generateResponses.isPending ? 'Generating...' : 'Generate Responses'}
        </Button>
        <Button
          onClick={() => generateFM312.mutate(auditPrepId)}
          disabled={generateFM312.isPending || total === 0}
        >
          <FileText className="h-4 w-4 mr-2" />
          {generateFM312.isPending ? 'Generating...' : 'Generate FM-312 PDF'}
        </Button>
        <div className="ml-auto text-sm text-gray-600">
          {answered} of {total} clauses answered
        </div>
      </div>

      {/* Progress summary */}
      {total > 0 && (
        <div className="flex flex-wrap gap-3">
          <Badge className="bg-green-100 text-green-800">Yes: {yesCount}</Badge>
          <Badge className="bg-red-100 text-red-800">No: {noCount}</Badge>
          <Badge className="bg-yellow-100 text-yellow-800">Partial: {partialCount}</Badge>
          <Badge className="bg-gray-100 text-gray-600">N/A: {total - answered}</Badge>
        </div>
      )}

      {/* Progress bar */}
      {total > 0 && (
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full transition-all"
            style={{ width: `${Math.round((answered / total) * 100)}%` }}
          />
        </div>
      )}

      {/* Clause cards */}
      {total === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <ClipboardCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No clause responses yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Click &quot;Generate Responses&quot; to auto-generate clause-by-clause responses from system data
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {responses.map((clause) => (
            <ClauseCard key={clause.id} clause={clause} auditPrepId={auditPrepId} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Tab
// ---------------------------------------------------------------------------

function SummaryTab({ responses }: { responses: AuditClauseResponse[] }) {
  const total = responses.length;
  if (total === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-gray-500">Generate responses first to see the summary.</p>
        </CardContent>
      </Card>
    );
  }

  const yesCount = responses.filter((r) => r.response === 'yes').length;
  const noCount = responses.filter((r) => r.response === 'no').length;
  const partialCount = responses.filter((r) => r.response === 'partial').length;
  const naCount = responses.filter((r) => r.response === 'not_applicable').length;
  const answered = total - naCount;
  const readinessScore = answered > 0 ? Math.round((yesCount / answered) * 100) : 0;

  const gaps = responses.filter((r) => r.response === 'no');
  const partials = responses.filter((r) => r.response === 'partial');

  return (
    <div className="space-y-6">
      {/* Readiness Score */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Overall Readiness</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 36 36">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke={readinessScore >= 80 ? '#16a34a' : readinessScore >= 50 ? '#d97706' : '#dc2626'}
                  strokeWidth="3"
                  strokeDasharray={`${readinessScore}, 100`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold">{readinessScore}%</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                <span className="text-gray-500">Compliant (Yes):</span>
                <span className="font-medium text-green-600">{yesCount}</span>
                <span className="text-gray-500">Non-compliant (No):</span>
                <span className="font-medium text-red-600">{noCount}</span>
                <span className="text-gray-500">Partial:</span>
                <span className="font-medium text-yellow-600">{partialCount}</span>
                <span className="text-gray-500">Not Applicable:</span>
                <span className="font-medium text-gray-400">{naCount}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gaps */}
      {gaps.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-lg text-red-700">
              Gaps ({gaps.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {gaps.map((g) => (
                <li key={g.id} className="flex items-start gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{g.clause_ref} — {g.clause_title}</span>
                    {g.evidence_description && (
                      <p className="text-gray-500 text-xs mt-0.5">{g.evidence_description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Action Items (Partial) */}
      {partials.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader>
            <CardTitle className="text-lg text-yellow-700">
              Action Items ({partials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {partials.map((p) => (
                <li key={p.id} className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{p.clause_ref} — {p.clause_title}</span>
                    {p.evidence_description && (
                      <p className="text-gray-500 text-xs mt-0.5">{p.evidence_description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {gaps.length === 0 && partials.length === 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-3" />
            <p className="text-green-800 font-medium">All clauses are compliant</p>
            <p className="text-sm text-green-600 mt-1">
              Your organization appears ready for the audit.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Detail Page
// ---------------------------------------------------------------------------

export function AuditPrepDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auditPrepId = Number(id);
  const [activeTab, setActiveTab] = useState<TabId>('fm333');

  const { data: auditPrep, isLoading } = useAuditPrep(auditPrepId, !!id);
  const { data: fm333Data, isLoading: fm333Loading } = useFM333Data(
    auditPrepId,
    !!id && activeTab === 'fm333'
  );

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!auditPrep) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-gray-500">Audit preparation not found.</p>
      </div>
    );
  }

  const statusClass = STATUS_BADGE[auditPrep.status] ?? 'bg-gray-100 text-gray-800';
  const responses = auditPrep.clause_responses ?? [];

  const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: 'fm333', label: 'FM-333', icon: <FileText className="h-4 w-4" /> },
    { id: 'fm312', label: 'FM-312', icon: <ClipboardCheck className="h-4 w-4" /> },
    { id: 'summary', label: 'Summary', icon: <BarChart3 className="h-4 w-4" /> },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/compliance/audit-prep')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getAuditTypeLabel(auditPrep.audit_type, auditPrep.audit_type_display)}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={statusClass}>{auditPrep.status_display}</Badge>
              {auditPrep.audit_date && (
                <span className="text-sm text-gray-500">
                  Target: {formatDateOnly(auditPrep.audit_date)}
                </span>
              )}
              {auditPrep.auditor_name && (
                <span className="text-sm text-gray-500">
                  Auditor: {auditPrep.auditor_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {auditPrep.generated_fm333_pdf && (
            <Button variant="outline" size="sm" onClick={() => downloadFM333(auditPrepId)}>
              <Download className="h-4 w-4 mr-2" />
              FM-333 PDF
            </Button>
          )}
          {auditPrep.generated_fm312_pdf && (
            <Button variant="outline" size="sm" onClick={() => downloadFM312(auditPrepId)}>
              <Download className="h-4 w-4 mr-2" />
              FM-312 PDF
            </Button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'fm333' && (
        <FM333Tab
          auditPrepId={auditPrepId}
          fm333Data={fm333Data}
          isLoading={fm333Loading}
        />
      )}
      {activeTab === 'fm312' && (
        <FM312Tab auditPrepId={auditPrepId} responses={responses} />
      )}
      {activeTab === 'summary' && <SummaryTab responses={responses} />}
    </div>
  );
}
